import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import ReservationCalendar from './ReservationCalendar';
import MiniCalendar from './MiniCalendar';
import RoomStatusModal from './RoomStatusModal';
import ServiceStatusModal from './ServiceStatusModal';
import RoomAssignmentModal from './RoomAssignmentModal';
import MonitorDoorCardV2 from '../Monitor/MonitorDoorCardV2';
import { getGuestIconUrl } from '../../../services/guestRoomIcons';
import moment from 'moment';

/**
 * `false` → `POST /getreservationprofile` with `room_assigned`.
 * `true` → `POST /getreservationprofileNEW` with `assignment_context` (`assign` | `show`) for the new contract.
 * Toggle here (not env) while the NEW route is in flight.
 */
const USE_RESERVATION_PROFILE_NEW_API = true;

const RESERVATION_PROFILE_PATH = USE_RESERVATION_PROFILE_NEW_API
    ? '/getreservationprofileNEW'
    : '/getreservationprofile';

function postReservationProfile(properties_id, confirmation_number, assignmentContext) {
    const ctx = assignmentContext === 'show' ? 'show' : 'assign';
    const body = USE_RESERVATION_PROFILE_NEW_API
        ? { properties_id, confirmation_number, assignment_context: ctx }
        : { properties_id, confirmation_number, room_assigned: ctx === 'show' };
    return ApiService.post(RESERVATION_PROFILE_PATH, body);
}

/** Door card: prefer real display text; fall back to status_name when API sends "N/A". */
function buildReservationProfileDoorCardData(roomData, extra = {}) {
    if (!roomData) return null;
    const rawDisplay = String(roomData.status_display_text ?? '').trim();
    const statusName = String(roomData.status_name ?? '').trim();
    const displayText =
        rawDisplay && rawDisplay.toUpperCase() !== 'N/A'
            ? rawDisplay
            : statusName || rawDisplay || 'Unknown';

    return {
        ...roomData,
        ...extra,
        status_display_text: displayText
    };
}

/**
 * @param {'assign'|'show'|null|undefined} assignmentContext - From room status monitor when known: skip probe call.
 *   'assign' → unassigned / recommendations (API: `assignment_context` or legacy `room_assigned: false`).
 *   'show' → reservation tied to a door/room on the monitor (`assignment_context` or `room_assigned: true`).
 *   Omit or null to probe (e.g. direct URL navigation): one `assign`-equivalent call, then `show` if a room is present.
 */
const ReservationProfile = ({
    show,
    onClose,
    confirmationNumber: confirmation_number,
    assignmentContext: assignmentContextProp
}) => {
    // iOS Safari (and some locked-down browsers) can throw or return non-JSON here; keep this resilient.
    const selectedLoginData = useMemo(() => {
        try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem('selectedLoginData') : null;
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            console.warn('Failed to read selectedLoginData from localStorage', e);
            return {};
        }
    }, []);
    const properties_id = selectedLoginData?.properties_id;

    const [loading, setLoading] = useState(true);
    const [reservationData, setReservationData] = useState(null);
    const [error, setError] = useState(null);

    // Status dropdown states
    const [roomStatusOptions, setRoomStatusOptions] = useState([]);
    const [serviceStatusOptions, setServiceStatusOptions] = useState([]);
    const [selectedRoomStatus, setSelectedRoomStatus] = useState('');
    const [selectedServiceStatus, setSelectedServiceStatus] = useState('');
    const [originalRoomStatus, setOriginalRoomStatus] = useState('');
    const [originalServiceStatus, setOriginalServiceStatus] = useState('');

    // Modal states (pending values ensure modal shows/sends the newly selected status, not stale state)
    const [showRoomStatusModal, setShowRoomStatusModal] = useState(false);
    const [pendingRoomStatusApi, setPendingRoomStatusApi] = useState('');
    const [pendingRoomStatusName, setPendingRoomStatusName] = useState('');
    const [showServiceStatusModal, setShowServiceStatusModal] = useState(false);
    const [showRoomAssignmentModal, setShowRoomAssignmentModal] = useState(false);

    // Room assignment states
    const [roomAssignments, setRoomAssignments] = useState([]);
    const [selectedRoomAssignment, setSelectedRoomAssignment] = useState(null);
    const [roomDataMap, setRoomDataMap] = useState({}); // Map room_number to full room data for door card
    const [assignedRoomData, setAssignedRoomData] = useState(null); // Full room data for assigned room door card
    /** When room is assigned: match row from room_assignment_array or synthetic { match_pct: 100, likes_checklist } for right-column card */
    const [assignedRoomSlotAssignment, setAssignedRoomSlotAssignment] = useState(null);
    const [roomAssignmentError, setRoomAssignmentError] = useState(null); // Store validation error message

    // API request loading states
    const [updatingRoomStatus, setUpdatingRoomStatus] = useState(false);
    const [updatingServiceStatus, setUpdatingServiceStatus] = useState(false);
    const [assigningRoom, setAssigningRoom] = useState(false);

    // Polling state for room assignment
    const [shouldPollForAssignment, setShouldPollForAssignment] = useState(false);

    /** True after "Change Assignment" — show the same recommendations UI as an unassigned reservation */
    const [reassignmentMode, setReassignmentMode] = useState(false);
    const [loadingReassignmentRecommendations, setLoadingReassignmentRecommendations] = useState(false);
    // Keep the most recent "eligible matches" list from unassigned flow so re-assign can reuse it.
    const [cachedEligibleAssignments, setCachedEligibleAssignments] = useState([]);

    // Backdrop-close handling:
    // iOS Safari can generate delayed/synthetic clicks after a tap; if the modal renders under the finger,
    // the backdrop can receive that click and immediately close the modal (making it look like it never opened).
    const ignoreBackdropCloseUntilRef = useRef(0);
    const backdropPressStartedRef = useRef(false);

    // room_status_service_array: per-date service status (read-only); must be at top level for rules-of-hooks
    const roomStatusServiceArray = useMemo(() => {
        const arr = reservationData?.room_status_service_array || [];
        return [...arr].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    }, [reservationData?.room_status_service_array]);

    // Fetch reservation profile data - extracted to be reusable
    const fetchReservationProfile = async (skipLoading = false) => {
        if (!properties_id || !confirmation_number) {
            setError('Missing required parameters');
            setLoading(false);
            return;
        }

        try {
            if (!skipLoading) {
                setLoading(true);
            }

            const explicitContext =
                assignmentContextProp === 'assign' || assignmentContextProp === 'show'
                    ? assignmentContextProp
                    : null;

            let response;
            let roomStatusResponse = null;
            let isRoomAssigned;

            if (explicitContext) {
                isRoomAssigned = explicitContext === 'show';
                if (isRoomAssigned) {
                    const [profileRes, roomStatusRes] = await Promise.all([
                        postReservationProfile(properties_id, confirmation_number, 'show'),
                        ApiService.post('/getroomstatuses', { properties_id })
                    ]);
                    response = profileRes;
                    roomStatusResponse = roomStatusRes;
                } else {
                    response = await postReservationProfile(properties_id, confirmation_number, 'assign');
                }
            } else {
                // Probe: no monitor context (e.g. standalone URL) — assign-first, then show if a room is already set.
                const checkResponse = await postReservationProfile(properties_id, confirmation_number, 'assign');

                if (!checkResponse?.data) {
                    setError('No data received from server');
                    setLoading(false);
                    return;
                }

                const checkReservationData = checkResponse.data?.reservation_info?.[0] || {};
                const checkRoomNumber = checkReservationData?.room_number;
                isRoomAssigned =
                    checkRoomNumber &&
                    checkRoomNumber !== null &&
                    checkRoomNumber !== '' &&
                    checkRoomNumber !== '0';

                response = checkResponse;
                if (isRoomAssigned) {
                    const [profileRes, roomStatusRes] = await Promise.all([
                        postReservationProfile(properties_id, confirmation_number, 'show'),
                        ApiService.post('/getroomstatuses', { properties_id })
                    ]);
                    response = profileRes;
                    roomStatusResponse = roomStatusRes;
                }
            }

            if (!response?.data) {
                setError('No data received from server');
                setLoading(false);
                return;
            }

            if (response?.data) {
                setReservationData(response.data);
                setError(null); // Clear any previous errors

                // Get reservation data - API returns reservation_info as array (may contain duplicates, use first entry)
                const reservationMainData = response.data?.reservation_info?.[0] || {};
                const roomNumber = reservationMainData?.room_number;
                // Check if room is assigned - room_number exists and is not null/empty/0
                // Use the value we already determined

                // Log reservation data for debugging (especially for unassigned reservations)
                if (!roomNumber || roomNumber === null || roomNumber === '') {
                    console.log('Unassigned reservation data:', {
                        confirmation_number,
                        reservationMainData,
                        hasArrivalDate: !!reservationMainData?.arrival_date_time,
                        hasDepartureDate: !!reservationMainData?.departure_date_time,
                        reservationStatus: reservationMainData?.reservation_status
                    });
                }

                // Set status options if available
                if (response.data?.room_status_info) {
                    setRoomStatusOptions(response.data.room_status_info);
                }
                if (response.data?.room_status_service_info) {
                    setServiceStatusOptions(response.data.room_status_service_info);
                }

                // Pre-populate room status dropdown from getreservationprofile when room is assigned
                // (API returns reservation_info[].room_status e.g. "DIRTY" — match to room_status_info and set selected)
                const profileRoomStatus = (reservationMainData?.room_status || '').toString().trim();
                if (isRoomAssigned && profileRoomStatus && response.data?.room_status_info?.length) {
                    const opts = response.data.room_status_info;
                    const p = profileRoomStatus.toUpperCase();
                    const matchByApi = opts.find(opt =>
                        (opt.status_name_api || '').toString().toUpperCase() === p
                    );
                    const matchByName = !matchByApi && opts.find(opt =>
                        (opt.status_name || '').toString().toUpperCase() === p
                    );
                    // e.g. "DIRTY" matches option with status_name "Vacant Dirty" or status_name_api containing "DIRTY"
                    const matchByContains = !matchByApi && !matchByName && opts.find(opt =>
                        ((opt.status_name_api || '').toString().toUpperCase().includes(p)) ||
                        ((opt.status_name || '').toString().toUpperCase().includes(p)) ||
                        (p.includes((opt.status_name_api || '').toString().toUpperCase())) ||
                        (p.includes((opt.status_name || '').toString().toUpperCase()))
                    );
                    const matchingOption = matchByApi || matchByName || matchByContains;
                    if (matchingOption?.status_name_api) {
                        setSelectedRoomStatus(matchingOption.status_name_api);
                        setOriginalRoomStatus(matchingOption.status_name_api);
                    }
                }

                // Get room_assignment_array from response if room is not assigned
                // Filter by confirmation_number since API returns recommendations for all unassigned reservations
                if (!isRoomAssigned && response.data?.room_assignment_array) {
                    // Filter room assignments for the current confirmation number
                    const filteredAssignments = response.data.room_assignment_array.filter(
                        assignment => assignment.confirmation_number === confirmation_number
                    );
                    setRoomAssignments(filteredAssignments);
                    setCachedEligibleAssignments(filteredAssignments);
                    // Clear any previous error when refreshing room assignments
                    setRoomAssignmentError(null);

                    // Fetch room data for each recommended room to display door card
                    if (filteredAssignments.length > 0) {
                        try {
                            const roomStatusResponse = await ApiService.post('/getroomstatuses', {
                                properties_id
                            });

                            if (roomStatusResponse?.data?.rooms_array) {
                                const roomMap = {};
                                filteredAssignments.forEach(assignment => {
                                    const roomData = roomStatusResponse.data.rooms_array.find(
                                        room => room.room_number === assignment.room_number
                                    );
                                    if (roomData) {
                                        roomMap[assignment.room_number] = roomData;
                                    }
                                });
                                setRoomDataMap(roomMap);
                            }
                        } catch (roomDataErr) {
                            console.error('Error fetching room data for recommendations:', roomDataErr);
                        }
                    } else {
                        setRoomDataMap({});
                    }
                } else {
                    setRoomAssignments([]);
                    setRoomDataMap({});
                }

                // Assigned reservation: build slot for right column from room_assignment_array or reservation_info[0]
                if (isRoomAssigned && roomNumber) {
                    const arr = response.data?.room_assignment_array || [];
                    const slotMatch = arr.find(
                        (a) =>
                            a.confirmation_number === confirmation_number &&
                            String(a.room_number) === String(roomNumber)
                    );
                    if (slotMatch) {
                        setAssignedRoomSlotAssignment({ ...slotMatch });
                    } else {
                        // Use assigned room data from reservation_info[0] (likes_checklist, matched_feature_array)
                        const likes = reservationMainData?.likes_checklist;
                        const matchedFeatures = reservationMainData?.matched_feature_array;
                        const matchPct =
                            Array.isArray(likes) && likes.length > 0
                                ? Math.round(
                                        (likes.filter((l) => l.is_match === 1).length / likes.length) * 100
                                    )
                                : 100;
                        setAssignedRoomSlotAssignment({
                            confirmation_number,
                            room_number: roomNumber,
                            match_pct: matchPct,
                            likes_checklist: Array.isArray(likes) ? likes : [],
                            matched_feature_array: Array.isArray(matchedFeatures) ? matchedFeatures : [],
                            pms_pk_id: reservationMainData?.pms_rooms_id,
                            external_id: reservationMainData?.external_id
                        });
                    }
                } else {
                    setAssignedRoomSlotAssignment(null);
                }

                // If room is assigned, use room status from the parallel fetch (or fetch if we didn't run parallel)
                if (isRoomAssigned) {
                    try {
                        const roomStatusRes = roomStatusResponse || await ApiService.post('/getroomstatuses', {
                            properties_id
                        });

                        if (roomStatusRes?.data?.rooms_array) {
                            // Find the room in the rooms array
                            const roomData = roomStatusRes.data.rooms_array.find(
                                room => room.room_number === roomNumber
                            );

                            if (roomData) {
                                // Service status is per-date (room_status_service_array), not a single current value — do not set selectedServiceStatus here.

                                // For room status, we need to find matching status_name_api from options
                                // The room data has status_name like "checkout clean complete" or "inspected and ready to occupy"
                                // We need to find the option that matches this status_name
                                let roomStatusApi = '';
                                if (roomData.status_name && response.data?.room_status_info) {
                                    // Try to find a matching option by status_name
                                    // Note: This might not always match perfectly, so we'll try to find the closest match
                                    const matchingOption = response.data.room_status_info.find(opt =>
                                        opt.status_name.toLowerCase() === roomData.status_name.toLowerCase() ||
                                        roomData.status_name.toLowerCase().includes(opt.status_name.toLowerCase())
                                    );

                                    if (matchingOption) {
                                        roomStatusApi = matchingOption.status_name_api;
                                    } else {
                                        // Fallback: try to infer from status_name
                                        // "checkout clean complete" might map to "CLEAN" or "INSPECTED"
                                        // "inspected and ready to occupy" should map to "INSPECTED"
                                        const statusNameLower = roomData.status_name.toLowerCase();
                                        if (statusNameLower.includes('inspect')) {
                                            roomStatusApi = 'INSPECTED';
                                        } else if (statusNameLower.includes('clean') && !statusNameLower.includes('dirty')) {
                                            roomStatusApi = 'CLEAN';
                                        } else if (statusNameLower.includes('dirty')) {
                                            roomStatusApi = 'DIRTY';
                                        } else if (statusNameLower.includes('pickup')) {
                                            roomStatusApi = 'PICKUP';
                                        }
                                    }
                                }

                                // Only overwrite dropdown when we resolved a value; otherwise keep profile value (reservation_info.room_status)
                                if (roomStatusApi) {
                                    setSelectedRoomStatus(roomStatusApi);
                                    setOriginalRoomStatus(roomStatusApi);
                                }
                                setAssignedRoomData(roomData);
                            }
                        } else {
                            setAssignedRoomData(null);
                        }
                    } catch (roomStatusErr) {
                        console.error('Error fetching room status:', roomStatusErr);
                        setAssignedRoomData(null);
                    }
                } else {
                    setAssignedRoomData(null);
                }

                if (isRoomAssigned) {
                    setReassignmentMode(false);
                }
            } else {
                setError('No data received from server');
                console.error('No data in API response:', response);
            }
        } catch (err) {
            console.error('Error fetching reservation profile:', err);
            console.error('Error details:', {
                confirmation_number,
                properties_id,
                error: err.message,
                response: err.response?.data
            });
            // Only show error if we don't have any data to display
            if (!reservationData) {
                setError('Failed to load reservation profile');
                toast.error('Failed to load reservation profile');
            } else {
                // If we have some data, just log the error but don't block the UI
                console.warn('Error occurred but reservation data is available');
            }
        } finally {
            if (!skipLoading) {
                setLoading(false);
            }
        }
    };

    /** Load possible room matches (same API path as unassigned recommendations) for re-assign flow */
    const fetchRoomRecommendationsForReassign = useCallback(async () => {
        if (!properties_id || !confirmation_number) {
            return;
        }
        const currentAssignedRoomNumber = reservationData?.reservation_info?.[0]?.room_number;
        const excludeCurrentAssignedRoom = (assignments = []) =>
            assignments.filter(
                (assignment) => String(assignment?.room_number) !== String(currentAssignedRoomNumber)
            );
        setLoadingReassignmentRecommendations(true);
        setRoomAssignmentError(null);
        try {
            // Prefer cached recommendations from the original unassigned flow.
            if (Array.isArray(cachedEligibleAssignments) && cachedEligibleAssignments.length > 0) {
                const filteredCachedAssignments = excludeCurrentAssignedRoom(cachedEligibleAssignments);
                setRoomAssignments(filteredCachedAssignments);
                if (filteredCachedAssignments.length > 0) {
                    const roomStatusResponse = await ApiService.post('/getroomstatuses', { properties_id });
                    if (roomStatusResponse?.data?.rooms_array) {
                        const roomMap = {};
                        filteredCachedAssignments.forEach((assignment) => {
                            const roomData = roomStatusResponse.data.rooms_array.find(
                                (room) => room.room_number === assignment.room_number
                            );
                            if (roomData) {
                                roomMap[assignment.room_number] = roomData;
                            }
                        });
                        setRoomDataMap(roomMap);
                    } else {
                        setRoomDataMap({});
                    }
                } else {
                    setRoomDataMap({});
                }
                return;
            }

            const response = await postReservationProfile(properties_id, confirmation_number, 'assign');
            const raw = response?.data?.room_assignment_array;
            if (!raw || !Array.isArray(raw)) {
                setRoomAssignments([]);
                setRoomDataMap({});
                return;
            }
            const filteredAssignments = raw.filter(
                (assignment) => assignment.confirmation_number === confirmation_number
            );
            const filteredWithoutAssigned = excludeCurrentAssignedRoom(filteredAssignments);
            setRoomAssignments(filteredWithoutAssigned);
            if (filteredWithoutAssigned.length > 0) {
                const roomStatusResponse = await ApiService.post('/getroomstatuses', { properties_id });
                if (roomStatusResponse?.data?.rooms_array) {
                    const roomMap = {};
                    filteredWithoutAssigned.forEach((assignment) => {
                        const roomData = roomStatusResponse.data.rooms_array.find(
                            (room) => room.room_number === assignment.room_number
                        );
                        if (roomData) {
                            roomMap[assignment.room_number] = roomData;
                        }
                    });
                    setRoomDataMap(roomMap);
                } else {
                    setRoomDataMap({});
                }
            } else {
                setRoomDataMap({});
            }
        } catch (e) {
            console.error('fetchRoomRecommendationsForReassign:', e);
            toast.error('Could not load room recommendations');
            setRoomAssignments([]);
            setRoomDataMap({});
        } finally {
            setLoadingReassignmentRecommendations(false);
        }
    }, [properties_id, confirmation_number, reservationData, cachedEligibleAssignments]);

    const handleStartReassignment = useCallback(async () => {
        setReassignmentMode(true);
        setRoomAssignments([]);
        setRoomDataMap({});
        await fetchRoomRecommendationsForReassign();
    }, [fetchRoomRecommendationsForReassign]);

    const handleCancelReassignment = useCallback(() => {
        setReassignmentMode(false);
        setRoomAssignments([]);
        setRoomDataMap({});
        setRoomAssignmentError(null);
        setLoadingReassignmentRecommendations(false);
    }, []);

    // Fetch reservation profile data on mount/change
    useEffect(() => {
        if (!show || !confirmation_number) {
            return;
        }

        fetchReservationProfile();
    }, [properties_id, confirmation_number, show, assignmentContextProp]);

    useEffect(() => {
        // Avoid leaking cached candidates between reservations.
        setCachedEligibleAssignments([]);
    }, [properties_id, confirmation_number]);

    useEffect(() => {
        if (!show) {
            setReassignmentMode(false);
            setLoadingReassignmentRecommendations(false);
        }
    }, [show]);

    // When opening, briefly ignore backdrop closes to avoid iOS "ghost click" immediately closing the modal.
    useEffect(() => {
        if (show) {
            // iOS can dispatch delayed synthetic mouse/click events after touch; keep a slightly longer guard.
            ignoreBackdropCloseUntilRef.current = Date.now() + 900;
            backdropPressStartedRef.current = false;
        }
    }, [show]);

    // Poll for room assignment every 5 seconds after assignment
    useEffect(() => {
        if (!shouldPollForAssignment || !show || !confirmation_number) {
            return;
        }

        let intervalId;

        const startPolling = () => {
            // Poll every 5 seconds
            intervalId = setInterval(() => {
                if (document.visibilityState === 'visible') {
                    fetchReservationProfile(true); // Skip loading state for polling
                }
            }, 5000);
        };

        if (document.visibilityState === 'visible') {
            startPolling();
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (!intervalId) {
                    startPolling();
                }
            } else {
                if (intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [shouldPollForAssignment, show, confirmation_number]);

    // Stop polling when room assignment appears
    useEffect(() => {
        if (shouldPollForAssignment && reservationData) {
            const reservationMainData = reservationData?.reservation_info?.[0] || {};
            const roomNumber = reservationMainData?.room_number;
            const hasRoom = roomNumber !== null && roomNumber !== undefined && roomNumber !== '' && roomNumber !== '0';

            if (hasRoom) {
                setShouldPollForAssignment(false);
            }
        }
    }, [reservationData, shouldPollForAssignment]);

    // Stop polling when modal closes
    useEffect(() => {
        if (!show) {
            setShouldPollForAssignment(false);
        }
    }, [show]);

    const assignedDoorCardData = useMemo(() => {
        if (!assignedRoomData) return null;
        const reservationMain = reservationData?.reservation_info?.[0] || {};
        return buildReservationProfileDoorCardData(assignedRoomData, {
            confirmation_number,
            checkin_today: reservationMain?.arrival_date_time
                ? moment(reservationMain.arrival_date_time).isSame(moment(), 'day')
                : false,
            checkout_today: reservationMain?.departure_date_time
                ? moment(reservationMain.departure_date_time).isSame(moment(), 'day')
                : false
        });
    }, [assignedRoomData, confirmation_number, reservationData]);

    // Handle room status change — only open confirm modal when user picks a valid new status
    const handleRoomStatusChange = (e) => {
        const newValue = (e.target.value || '').trim();
        setSelectedRoomStatus(newValue);

        // Don't open modal for empty "Select Room Status"; reset to original
        if (!newValue) {
            setSelectedRoomStatus(originalRoomStatus);
            return;
        }
        // Only show confirm modal when value actually changed from original
        if (newValue !== originalRoomStatus) {
            const name = roomStatusOptions.find(opt => opt.status_name_api === newValue)?.status_name || newValue;
            setPendingRoomStatusApi(newValue);
            setPendingRoomStatusName(name);
            setShowRoomStatusModal(true);
        }
    };

    // Handle service status change
    const handleServiceStatusChange = (e) => {
        const newValue = e.target.value;
        setSelectedServiceStatus(newValue);

        // If value changed from original, show modal
        if (newValue !== originalServiceStatus) {
            setShowServiceStatusModal(true);
        }
    };

    // Handle room status update success
    const handleRoomStatusUpdateSuccess = () => {
        setOriginalRoomStatus(selectedRoomStatus);
        setPendingRoomStatusApi('');
        setPendingRoomStatusName('');
        setShowRoomStatusModal(false);
        toast.success('Room status updated successfully');
    };

    // Handle service status update success
    const handleServiceStatusUpdateSuccess = () => {
        setOriginalServiceStatus(selectedServiceStatus);
        setShowServiceStatusModal(false);
        toast.success('Service status updated successfully');
    };

    // Handle modal cancel - reset to original value
    const handleRoomStatusCancel = () => {
        setSelectedRoomStatus(originalRoomStatus);
        setPendingRoomStatusApi('');
        setPendingRoomStatusName('');
        setShowRoomStatusModal(false);
    };

    const handleServiceStatusCancel = () => {
        setSelectedServiceStatus(originalServiceStatus);
        setShowServiceStatusModal(false);
    };

    // Handle room assignment
    const handleAssignRoom = (assignment) => {
        setSelectedRoomAssignment(assignment);
        setShowRoomAssignmentModal(true);
    };

    const handleRoomAssignmentSuccess = () => {
        setShowRoomAssignmentModal(false);
        setSelectedRoomAssignment(null);
        // Start polling for room assignment to appear
        setShouldPollForAssignment(true);
    };

    const handleRoomAssignmentCancel = () => {
        setShowRoomAssignmentModal(false);
        setSelectedRoomAssignment(null);
    };

    // Handle room assignment validation error - refresh room list
    const handleRoomAssignmentValidationError = async (errorMessage) => {
        setShowRoomAssignmentModal(false);
        setSelectedRoomAssignment(null);

        // Set the error message to display in Room Recommendations section
        setRoomAssignmentError(errorMessage || 'Room not available to assign');

        // Refresh room assignments list for unassigned reservations
        if (!properties_id || !confirmation_number) {
            return;
        }

        try {
            // Fetch updated room recommendations
            const response = await postReservationProfile(properties_id, confirmation_number, 'assign');

            if (response?.data?.room_assignment_array) {
                // Filter room assignments for the current confirmation number
                const filteredAssignments = response.data.room_assignment_array.filter(
                    assignment => assignment.confirmation_number === confirmation_number
                );
                setRoomAssignments(filteredAssignments);

                // Fetch room data for each recommended room to display door card
                if (filteredAssignments.length > 0) {
                    try {
                        const roomStatusResponse = await ApiService.post('/getroomstatuses', {
                            properties_id
                        });

                        if (roomStatusResponse?.data?.rooms_array) {
                            const roomMap = {};
                            filteredAssignments.forEach(assignment => {
                                const roomData = roomStatusResponse.data.rooms_array.find(
                                    room => room.room_number === assignment.room_number
                                );
                                if (roomData) {
                                    roomMap[assignment.room_number] = roomData;
                                }
                            });
                            setRoomDataMap(roomMap);
                        }
                    } catch (roomDataErr) {
                        console.error('Error fetching room data for recommendations:', roomDataErr);
                    }
                } else {
                    setRoomDataMap({});
                }
            } else {
                setRoomAssignments([]);
                setRoomDataMap({});
            }
        } catch (err) {
            console.error('Error refreshing room assignments:', err);
        }
    };

    // Show loading spinner inside modal overlay so it's visible while profile loads
    if (loading && show) {
        return (
            <div
                className="modal show d-block"
                tabIndex={-1}
                role="dialog"
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 2000,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    padding: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content shadow">
                        <div className="modal-body d-flex flex-column align-items-center justify-content-center py-5">
                            <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mb-0 text-muted">Loading reservation profile...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return null;
    }

    if (error && !reservationData) {
        return (
            <div className="card shadow-sm border-0">
                <div className="card-body">
                    <div className="alert alert-danger" role="alert">
                        {error || 'Reservation not found'}
                    </div>
                </div>
            </div>
        );
    }

    if (!reservationData) {
        return null;
    }

    // Get reservation data from the array - API returns reservation_info as array
    const reservationMainData = reservationData?.reservation_info?.[0] || {};
    // activity_log_array is at root level, not inside reservation_info
    const activityLogArray = reservationData?.activity_log_array || [];

    const roomNumber = reservationMainData?.room_number;
    const hasRoom = roomNumber !== null && roomNumber !== undefined && roomNumber !== '';

    const guestFirstRaw = (reservationMainData?.guest_first || '').toString().trim();
    const guestLastRaw = (reservationMainData?.guest_last || '').toString().trim();
    const guestDisplayName = [guestFirstRaw, guestLastRaw].filter(Boolean).join(' ');

    // Get selected status names for display
    const selectedRoomStatusName = roomStatusOptions.find(opt => opt.status_name_api === selectedRoomStatus)?.status_name || '';
    const selectedServiceStatusName = serviceStatusOptions.find(opt => opt.status_name_api === selectedServiceStatus)?.status_name || '';

    // Format reservation status for display
    const formatReservationStatus = (status) => {
        if (!status) return 'N/A';
        return status
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (!show) {
        return null;
    }

    return (
        <div
            className="modal show d-block"
            tabIndex={-1}
            role="dialog"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 2000,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                padding: '0.75rem'
            }}
            onMouseDown={(e) => {
                backdropPressStartedRef.current = (e.target === e.currentTarget);
            }}
            onMouseUp={(e) => {
                const isBackdrop = e.target === e.currentTarget;
                const ignore = Date.now() < ignoreBackdropCloseUntilRef.current;
                if (!ignore && isBackdrop && backdropPressStartedRef.current) {
                    onClose();
                }
                backdropPressStartedRef.current = false;
            }}
            onTouchStart={(e) => {
                backdropPressStartedRef.current = (e.target === e.currentTarget);
            }}
            onTouchEnd={(e) => {
                const isBackdrop = e.target === e.currentTarget;
                const ignore = Date.now() < ignoreBackdropCloseUntilRef.current;
                if (!ignore && isBackdrop && backdropPressStartedRef.current) {
                    onClose();
                }
                backdropPressStartedRef.current = false;
            }}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable modal-fullscreen-lg-down"
                role="document"
                style={{
                    // Avoid CSS min() for older iOS; keep desktop sizing while allowing small screens to breathe.
                    maxWidth: 1100,
                    width: 'calc(100% - 0.5rem)'
                }}
            >
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">Reservation Profile</h5>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={onClose}
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            {/* Left Column - Max 50% width */}
                            <div className="col-lg-6">
                                {/* Top Section - Confirmation Number */}
                                <div className="mb-4">
                                    <div className="mb-3">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <h3 className="fw-bold mb-0">Confirmation Number : </h3>
                                            <h3 className='fw-bold text-primary mb-0'>{confirmation_number}</h3>
                                        </div>
                                        {guestDisplayName && (
                                            <div className="mt-2">
                                                <span className="text-muted small text-uppercase fw-semibold d-block mb-1">
                                                    Guest
                                                </span>
                                                <span className="fs-5 fw-semibold text-dark">{guestDisplayName}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Room Type */}
                                    {reservationMainData?.room_type_name && (
                                        <div className="mb-3">
                                            <h3 className="fw-bold mb-0">
                                                Room Type: {reservationMainData.room_type_name}
                                                {reservationMainData?.room_type_code && (
                                                    <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                        {' '}({reservationMainData.room_type_code})
                                                    </span>
                                                )}
                                            </h3>
                                        </div>
                                    )}

                                    {/* Guest Status Section - Always show for all reservations */}
                                    <div className="mb-3">
                                        <h6 className="mb-3 fw-bold">Guest Status</h6>
                                        <div className="d-flex flex-column gap-3">
                                            {reservationMainData?.adults_count > 0 && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <strong>Adults:</strong>
                                                    <div className="d-flex gap-1">
                                                        {Array.from({ length: reservationMainData.adults_count }).map((_, index) => (
                                                            <img
                                                                key={index}
                                                                src={getGuestIconUrl(reservationMainData?.guest_is_vip)}
                                                                alt="Adult"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {reservationMainData?.children_count > 0 && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <strong>Children:</strong>
                                                    <div className="d-flex gap-1">
                                                        {Array.from({ length: reservationMainData.children_count }).map((_, index) => (
                                                            <img
                                                                key={index}
                                                                src={getGuestIconUrl(reservationMainData?.guest_is_vip)}
                                                                alt="Child"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {reservationMainData?.infants_count > 0 && (
                                                <div className="d-flex align-items-center gap-2">
                                                    <strong>Infants:</strong>
                                                    <div className="d-flex gap-1">
                                                        {Array.from({ length: reservationMainData.infants_count }).map((_, index) => (
                                                            <img
                                                                key={index}
                                                                src={getGuestIconUrl(reservationMainData?.guest_is_vip)}
                                                                alt="Infant"
                                                                style={{ width: '24px', height: '24px' }}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {reservationMainData?.guest_is_vip === true && (
                                                <div>
                                                    <strong className="text-primary" style={{ fontSize: '1.1em' }}>VIP</strong>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Reservation Status */}
                                    <div className="mb-2">
                                        <h4 className="fw-bold mb-0">Reservation Status: <font className='text-primary'>{formatReservationStatus(reservationMainData?.reservation_status)}</font></h4>
                                    </div>
                                </div>

                                {/* Mini Calendar + arrival/departure — all reservations (assigned room card is on the right) */}
                                {reservationMainData?.arrival_date_time && reservationMainData?.departure_date_time && (
                                    <div className="mb-3 d-flex flex-column flex-sm-row gap-3 align-items-start">
                                        {/* Mini Calendar */}
                                        <div>
                                            <MiniCalendar
                                                arrivalDate={reservationMainData.arrival_date_time}
                                                departureDate={reservationMainData.departure_date_time}
                                            />
                                        </div>

                                        {/* Arrival and Departure - To the right of calendar, stacked vertically */}
                                        <div className="d-flex flex-column gap-4">
                                            {/* add label for arrival and departure */}
                                            <div className="d-flex flex-column align-items-start gap-2">
                                                <span className="text-muted">Arrival:</span>
                                                {/* Arrival - Green background */}
                                                <div
                                                    style={{
                                                        backgroundColor: '#28a745',
                                                        color: '#fff',
                                                        padding: '8px 12px',
                                                        borderRadius: '4px',
                                                        fontWeight: '500',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {reservationMainData.arrival_date_time
                                                        ? moment(reservationMainData.arrival_date_time).format('YYYY-MM-DD hh:mm A')
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                            <div className="d-flex flex-column align-items-start gap-2">
                                                <span className="text-muted">Departure:</span>
                                                {/* Departure - Red background */}
                                                <div
                                                    style={{
                                                        backgroundColor: '#dc3545',
                                                        color: '#fff',
                                                        padding: '8px 12px',
                                                        borderRadius: '4px',
                                                        fontWeight: '500',
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {reservationMainData.departure_date_time
                                                        ? moment(reservationMainData.departure_date_time).format('YYYY-MM-DD hh:mm A')
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Current Room Status dropdown and read-only scheduled service status list - Only show if room is assigned */}
                                {hasRoom && (
                                    <div className="mb-4">
                                        <div className="mb-3">
                                            <label htmlFor="roomStatus" className="form-label fw-bold">
                                                Current Room Status
                                            </label>
                                            <select
                                                id="roomStatus"
                                                className="form-select"
                                                value={selectedRoomStatus}
                                                onChange={handleRoomStatusChange}
                                                disabled={updatingRoomStatus || updatingServiceStatus}
                                                style={{ color: '#212529', fontWeight: '500', maxWidth: '280px' }}
                                            >
                                                <option value="" style={{ color: '#6c757d' }}>Select Room Status</option>
                                                {roomStatusOptions.map((option, index) => (
                                                    <option
                                                        key={index}
                                                        value={option.status_name_api}
                                                        style={{ color: '#212529', fontWeight: '500' }}
                                                    >
                                                        {option.status_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Read-only list: reservation dates and scheduled service status */}
                                        {roomStatusServiceArray.length > 0 && (
                                            <div className="mt-3">
                                                <label className="form-label fw-bold mb-2">Scheduled service status by date</label>
                                                <ul className="list-group" style={{ maxWidth: '400px' }}>
                                                    {roomStatusServiceArray.map((item, index) => {
                                                        const isInService = (item.service_status || '').toUpperCase() === 'IN_SERVICE';
                                                        const displayStatus = serviceStatusOptions.find(opt =>
                                                            (opt.status_name_api || '').toUpperCase() === (item.service_status || '').toUpperCase()
                                                        )?.status_name || (item.service_status || '').replace(/_/g, ' ');
                                                        const dateLabel = item.date ? moment(item.date).format('MMM D, YYYY') : item.date || '—';
                                                        return (
                                                            <li
                                                                key={index}
                                                                className="list-group-item d-flex justify-content-between align-items-center py-2"
                                                                style={{
                                                                    backgroundColor: isInService ? undefined : 'rgba(220, 53, 69, 0.08)',
                                                                    borderLeft: `3px solid ${isInService ? '#495057' : '#dc3545'}`
                                                                }}
                                                            >
                                                                <span style={{ fontWeight: '500' }}>{dateLabel}</span>
                                                                <span
                                                                    style={{
                                                                        fontWeight: '500',
                                                                        color: isInService ? '#495057' : '#dc3545'
                                                                    }}
                                                                >
                                                                    {displayStatus}
                                                                </span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}


                                {/* Activity Log Section - Moved to left column under all content */}
                                {activityLogArray && activityLogArray.length > 0 && (() => {
                                    // Sort activity log chronologically
                                    const sortedLogs = [...activityLogArray].sort((a, b) => {
                                        const dateA = a.event_timestamp
                                            ? moment(a.event_timestamp)
                                            : moment(0);
                                        const dateB = b.event_timestamp
                                            ? moment(b.event_timestamp)
                                            : moment(0);
                                        return dateB.valueOf() - dateA.valueOf();
                                    });

                                    const formatEventName = (eventName) => {
                                        if (!eventName) return 'N/A';
                                        return eventName
                                            .split('_')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                            .join(' ');
                                    };

                                    return (
                                        <div className="mb-4">
                                            <h6 className="mb-2 fw-bold">Activity Log</h6>
                                            <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                <table className="table table-sm table-bordered">
                                                    <thead className="table-light sticky-top">
                                                        <tr>
                                                            <th style={{ fontSize: '0.85rem' }}>Date/Time</th>
                                                            <th style={{ fontSize: '0.85rem' }}>Activity</th>
                                                            <th style={{ fontSize: '0.85rem' }}>Room</th>
                                                            <th style={{ fontSize: '0.85rem' }}>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {sortedLogs.map((log, index) => (
                                                            <tr key={index} className='text-black'>
                                                                <td style={{ fontSize: '0.8rem' }}>
                                                                    {log.event_timestamp
                                                                        ? moment(log.event_timestamp).format('MMM Do, YYYY HH:mm')
                                                                        : 'N/A'}
                                                                </td>
                                                                <td style={{ fontSize: '0.8rem' }}>{formatEventName(log.event_name)}</td>
                                                                <td style={{ fontSize: '0.8rem' }}>{log.room_number || 'N/A'}</td>
                                                                <td style={{ fontSize: '0.8rem' }}>
                                                                    {log.reservation_status
                                                                        ? formatReservationStatus(log.reservation_status)
                                                                        : 'N/A'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Right Column */}
                            <div className="col-lg-6">
                                {reassignmentMode && loadingReassignmentRecommendations && (
                                    <div className="mb-4 text-center py-5">
                                        <div className="spinner-border text-primary mb-3" role="status">
                                            <span className="visually-hidden">Loading…</span>
                                        </div>
                                        <p className="text-muted mb-0">Loading room matches…</p>
                                    </div>
                                )}

                                {reassignmentMode &&
                                    !loadingReassignmentRecommendations &&
                                    roomAssignments.length === 0 && (
                                        <div className="mb-4 alert border d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-2">
                                            <span className="mb-0">No room matches were returned for this reservation.</span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                                onClick={handleCancelReassignment}
                                            >
                                                Back to assigned room
                                            </button>
                                        </div>
                                    )}

                                {/* Room Recommendations — unassigned, or assigned user in re-assign mode */}
                                {roomAssignments.length > 0 &&
                                    (!hasRoom || reassignmentMode) &&
                                    !loadingReassignmentRecommendations && (
                                    <div className="mb-4">
                                        {reassignmentMode && (
                                            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
                                                <span className="text-muted small mb-0">
                                                    Pick a room below to change assignment, or cancel to keep the current room.
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary flex-shrink-0"
                                                    onClick={handleCancelReassignment}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}

                                        {/* Reserved Room Type Header */}
                                        {roomDataMap && Object.values(roomDataMap).map((roomData, index) => (
                                            index === 0 && <div key={index} className="mb-3">
                                                <h4 className="fw-bold mb-0">
                                                    Reserved Room Type :
                                                    {' ' + roomData.room_type_name}
                                                    {roomData.room_type_code && (
                                                        <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                            {' '}({roomData.room_type_code})
                                                        </span>
                                                    )}
                                                </h4>
                                            </div>
                                        ))}
                                        {/* {reservationMainData?.room_type_name && (
                                            <div className="mb-3">
                                                <h6 className="fw-bold mb-0">
                                                    Reserved Room Type: {reservationMainData.room_type_name}
                                                    {reservationMainData?.room_type_code && (
                                                        <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                            {' '}({reservationMainData.room_type_code})
                                                        </span>
                                                    )}
                                                </h6>
                                            </div>
                                        )} */}
                                        <h6 className="mb-3 fw-bold">Room Recommendations</h6>

                                        {/* Validation Error Message - Show above recommendations */}
                                        {roomAssignmentError && (
                                            <div
                                                className="alert alert-danger mb-3"
                                                role="alert"
                                                style={{
                                                    fontSize: '0.9rem',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '4px'
                                                }}
                                            >
                                                <i className="bi bi-exclamation-triangle me-2"></i>
                                                {roomAssignmentError}
                                            </div>
                                        )}

                                        <div className="d-flex flex-column gap-3">
                                            {roomAssignments.map((assignment, index) => {
                                                const roomData = roomDataMap[assignment.room_number];
                                                const cardData = buildReservationProfileDoorCardData(roomData);
                                                return (
                                                    <div
                                                        key={index}
                                                        className="card shadow-sm border"
                                                        style={{
                                                            borderLeft: '4px solid #0d6efd',
                                                            borderRadius: '8px'
                                                        }}
                                                    >
                                                        <div className="card-body p-3">
                                                            <div className="d-flex flex-column flex-sm-row align-items-start gap-3">
                                                                {/* Match Percentage - On the left of door */}
                                                                <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: '80px' }}>
                                                                    <div
                                                                        style={{
                                                                            fontSize: '1.5rem',
                                                                            fontWeight: 'bold',
                                                                            color: '#06A9F4',
                                                                            marginBottom: '4px'
                                                                        }}
                                                                    >
                                                                        {assignment.match_pct}%
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: '0.75rem',
                                                                            color: '#6c757d',
                                                                            fontWeight: '500'
                                                                        }}
                                                                    >
                                                                        feature
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: '0.75rem',
                                                                            color: '#6c757d',
                                                                            fontWeight: '500'
                                                                        }}
                                                                    >
                                                                        match
                                                                    </div>
                                                                </div>

                                                                {/* Door card for recommended room */}
                                                                <div className="d-flex flex-column align-items-center">
                                                                    {cardData ? (
                                                                        <MonitorDoorCardV2
                                                                            data={cardData}
                                                                            roomNumber={assignment.room_number}
                                                                            status={cardData.status_display_text}
                                                                            statusColor={roomData.status_display_backcolor}
                                                                            isDnd={false}
                                                                            mbb="mb-0"
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="border rounded p-2 text-center"
                                                                            style={{
                                                                                backgroundColor: '#f8f9fa',
                                                                                borderColor: '#dee2e6',
                                                                                borderWidth: '2px',
                                                                                minWidth: '80px',
                                                                                fontSize: '1.2rem',
                                                                                fontWeight: 'bold',
                                                                                color: '#212529'
                                                                            }}
                                                                        >
                                                                            {assignment.room_number}
                                                                        </div>
                                                                    )}

                                                                    <button
                                                                        className="btn btn-primary btn-sm mt-2"
                                                                        onClick={() => handleAssignRoom(assignment)}
                                                                        disabled={assigningRoom}
                                                                        style={{ whiteSpace: 'nowrap' }}
                                                                    >
                                                                        Assign
                                                                    </button>
                                                                </div>

                                                                {/* Guest Likes List */}
                                                                <div className="flex-grow-1">
                                                                    {/* Room Type - Display above Guest Likes */}
                                                                    {roomData?.room_type_name && (
                                                                        <div className="mb-2">
                                                                            <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem', color: '#212529' }}>
                                                                                {/* <i className="bi bi-check" style={{ color: '#28a745', fontSize: '0.9rem', marginRight: '4px' }}></i> */}
                                                                                {roomData.room_type_name}
                                                                                {roomData.room_type_code && (
                                                                                    <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                                                        {' '}({roomData.room_type_code})
                                                                                    </span>
                                                                                )}
                                                                            </h6>
                                                                        </div>
                                                                    )}
                                                                    <label
                                                                        className="fw-bold mb-2"
                                                                        style={{ fontSize: '0.85rem', color: '#6c757d' }}
                                                                    >
                                                                        Matches these Guest "Likes"
                                                                    </label>
                                                                    {assignment.likes_checklist && assignment.likes_checklist.length > 0 ? (
                                                                        <ul className="mb-0 assignRoom" style={{ fontSize: '0.85rem', paddingLeft: '0px!important', listStyleType: 'none', listStylePosition: 'outside' }}>
                                                                            {(() => {
                                                                                // Sort likes: matched (is_match: 1) first, then unmatched (is_match: 0)
                                                                                const sortedLikes = [...assignment.likes_checklist].sort((a, b) => {
                                                                                    // Matched items (1) come before unmatched items (0)
                                                                                    return (b.is_match || 0) - (a.is_match || 0);
                                                                                });
                                                                                const firstNonMatchIndex = sortedLikes.findIndex(like => like.is_match !== 1);

                                                                                return sortedLikes.map((like, likeIndex) => (
                                                                                    <li key={likeIndex} style={{ marginBottom: '4px', paddingLeft: '0' }}>
                                                                                        {like.is_match === 1 ? (
                                                                                            <span className='text-muted'>
                                                                                                <i className="bi bi-check" style={{ color: '#28a745', fontSize: '1.25rem', marginRight: '5px', opacity: 1 }}></i>
                                                                                                {like.like_name}
                                                                                            </span>
                                                                                        ) : (
                                                                                            <div className='d-flex flex-column'>

                                                                                                {/* {likeIndex === firstNonMatchIndex && (
                                                                                                    <label
                                                                                                        className="mt-2 fw-bold mb-2"
                                                                                                        style={{ fontSize: '0.85rem', color: '#6c757d' }}
                                                                                                    >
                                                                                                        Does Not Match These Guest "Likes"
                                                                                                    </label>
                                                                                                )} */}
                                                                                                <span className='text-muted'>
                                                                                                    <i className="bi bi-x" style={{ color: '#dc3545', fontSize: '1.25rem', marginRight: '5px', opacity: 1 }}></i>
                                                                                                    {like.like_name}
                                                                                                </span>
                                                                                            </div>

                                                                                        )}
                                                                                    </li>
                                                                                ));
                                                                            })()}
                                                                        </ul>
                                                                    ) : (
                                                                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                                                            No guest likes available
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                {/* Assign Button */}
                                                                {/* <div className="d-flex align-items-center">
                                                                    <button
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={() => handleAssignRoom(assignment)}
                                                                        disabled={assigningRoom}
                                                                        style={{ whiteSpace: 'nowrap' }}
                                                                    >
                                                                        Assign
                                                                    </button>
                                                                </div> */}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Assigned reservation — same right-column layout as recommendations */}
                                {hasRoom && assignedRoomSlotAssignment && !reassignmentMode && (
                                    <div className="mb-4">
                                        <div className="mb-3">
                                            <h4 className="fw-bold mb-0">
                                                Reserved Room Type :
                                                {' ' + (reservationMainData.room_type_name || 'N/A')}
                                                {reservationMainData.room_type_code && (
                                                    <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                        {' '}({reservationMainData.room_type_code})
                                                    </span>
                                                )}
                                            </h4>
                                        </div>
                                        <h6 className="mb-3 fw-bold">Assigned Room:</h6>
                                        <div
                                            className="card shadow-sm border"
                                            style={{
                                                borderLeft: '4px solid #20c997',
                                                borderRadius: '8px'
                                            }}
                                        >
                                            <div className="card-body p-3">
                                                <div className="d-flex flex-column flex-sm-row align-items-start">
                                                    <div className="d-flex flex-column align-items-center justify-content-center" style={{ minWidth: '80px', height: '-webkit-fill-available' }}>
                                                        <div
                                                            style={{
                                                                fontSize: '1.5rem',
                                                                fontWeight: 'bold',
                                                                color: '#06A9F4',
                                                                marginBottom: '4px'
                                                            }}
                                                        >
                                                            {assignedRoomSlotAssignment.match_pct ?? 100}%
                                                        </div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: '500' }}>feature</div>
                                                        <div style={{ fontSize: '0.75rem', color: '#6c757d', fontWeight: '500' }}>match</div>
                                                    </div>
                                                    <div className="d-flex flex-column align-items-center">
                                                        {assignedDoorCardData ? (
                                                            <MonitorDoorCardV2
                                                                data={assignedDoorCardData}
                                                                roomNumber={assignedRoomSlotAssignment.room_number}
                                                                status={assignedDoorCardData.status_display_text}
                                                                statusColor={assignedRoomData.status_display_backcolor}
                                                                isDnd={false}
                                                                mbb="mt-3 mb-0"
                                                            />
                                                        ) : (
                                                            <div
                                                                className="border rounded p-2 text-center"
                                                                style={{
                                                                    backgroundColor: '#f8f9fa',
                                                                    borderColor: '#dee2e6',
                                                                    borderWidth: '2px',
                                                                    minWidth: '80px',
                                                                    fontSize: '1.2rem',
                                                                    fontWeight: 'bold',
                                                                    color: '#212529'
                                                                }}
                                                            >
                                                                {assignedRoomSlotAssignment.room_number}
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm mt-2 btn-secondary"
                                                            style={{
                                                                // backgroundColor: '#20c997',
                                                                // borderColor: '#20c997',
                                                                color: '#fff',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                            onClick={() => handleStartReassignment()}
                                                        >
                                                            Re-Assign
                                                        </button>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        {(assignedRoomData?.room_type_name || reservationMainData?.room_type_name) && (
                                                            <div className="mb-2">
                                                                <h6 className="fw-bold mb-0" style={{ fontSize: '0.9rem', color: '#212529' }}>
                                                                    {assignedRoomData?.room_type_name || reservationMainData.room_type_name}
                                                                    {(assignedRoomData?.room_type_code || reservationMainData?.room_type_code) && (
                                                                        <span className="text-muted" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                                            {' '}({assignedRoomData?.room_type_code || reservationMainData.room_type_code})
                                                                        </span>
                                                                    )}
                                                                </h6>
                                                            </div>
                                                        )}
                                                        <label
                                                            className="fw-bold mb-2 d-block"
                                                            style={{ fontSize: '0.85rem', color: '#6c757d' }}
                                                        >
                                                            Matches these Guest &quot;Likes&quot;
                                                        </label>
                                                        {assignedRoomSlotAssignment.likes_checklist &&
                                                            assignedRoomSlotAssignment.likes_checklist.length > 0 ? (
                                                            <ul
                                                                className="mb-0 assignRoom"
                                                                style={{
                                                                    fontSize: '0.85rem',
                                                                    paddingLeft: '0px!important',
                                                                    listStyleType: 'none',
                                                                    listStylePosition: 'outside'
                                                                }}
                                                            >
                                                                {[...assignedRoomSlotAssignment.likes_checklist]
                                                                    .sort((a, b) => (b.is_match || 0) - (a.is_match || 0))
                                                                    .map((like, likeIndex) => (
                                                                        <li key={likeIndex} style={{ marginBottom: '4px', paddingLeft: '0' }}>
                                                                            {like.is_match === 1 ? (
                                                                                <span className="text-muted">
                                                                                    <i
                                                                                        className="bi bi-check"
                                                                                        style={{
                                                                                            color: '#28a745',
                                                                                            fontSize: '1.25rem',
                                                                                            marginRight: '5px',
                                                                                            opacity: 1
                                                                                        }}
                                                                                    />
                                                                                    {like.like_name}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-muted">
                                                                                    <i
                                                                                        className="bi bi-x"
                                                                                        style={{
                                                                                            color: '#dc3545',
                                                                                            fontSize: '1.25rem',
                                                                                            marginRight: '5px',
                                                                                            opacity: 1
                                                                                        }}
                                                                                    />
                                                                                    {like.like_name}
                                                                                </span>
                                                                            )}
                                                                        </li>
                                                                    ))}
                                                            </ul>
                                                        ) : (
                                                            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                                                No guest likes available
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                    {/* Close Button - Bottom Right */}
                    <div className="modal-footer d-flex justify-content-end" style={{ borderTop: '1px solid #dee2e6', padding: '0.75rem 1rem' }}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Room Status Modal - external_id from reservation/room; fallback to pms_rooms_id when API doesn't return external_id */}
            {showRoomStatusModal && (() => {
                const externalId = reservationMainData?.external_id
                    ?? assignedRoomData?.external_id
                    ?? reservationMainData?.pms_rooms_id
                    ?? assignedRoomData?.pms_rooms_id;
                return (
                    <RoomStatusModal
                        show={showRoomStatusModal}
                        onClose={handleRoomStatusCancel}
                        onSuccess={handleRoomStatusUpdateSuccess}
                        onLoadingChange={setUpdatingRoomStatus}
                        statusName={pendingRoomStatusName || selectedRoomStatusName}
                        statusNameApi={pendingRoomStatusApi || selectedRoomStatus}
                        externalId={externalId}
                        propertiesId={properties_id}
                    />
                );
            })()}

            {/* Service Status Modal */}
            {showServiceStatusModal && (
                <ServiceStatusModal
                    show={showServiceStatusModal}
                    onClose={handleServiceStatusCancel}
                    onSuccess={handleServiceStatusUpdateSuccess}
                    onLoadingChange={setUpdatingServiceStatus}
                    statusName={selectedServiceStatusName}
                    statusNameApi={selectedServiceStatus}
                    externalId={reservationMainData?.external_id ?? assignedRoomData?.external_id ?? reservationMainData?.pms_rooms_id ?? assignedRoomData?.pms_rooms_id}
                    propertiesId={properties_id}
                    fromDate={reservationMainData?.arrival_date_time ? moment(reservationMainData.arrival_date_time).format('YYYY-MM-DD') : null}
                    toDate={reservationMainData?.departure_date_time ? moment(reservationMainData.departure_date_time).format('YYYY-MM-DD') : null}
                />
            )}

            {/* Room Assignment Modal */}
            {showRoomAssignmentModal && selectedRoomAssignment && (
                <RoomAssignmentModal
                    show={showRoomAssignmentModal}
                    onClose={handleRoomAssignmentCancel}
                    onSuccess={handleRoomAssignmentSuccess}
                    onValidationError={handleRoomAssignmentValidationError}
                    onLoadingChange={setAssigningRoom}
                    roomNumber={selectedRoomAssignment.room_number}
                    confirmationNumber={confirmation_number}
                    pmsPkId={selectedRoomAssignment.pms_pk_id}
                    externalId={selectedRoomAssignment.external_id}
                />
            )}
        </div>
    );
};

export default ReservationProfile;

